import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button, SearchBar } from '@/components/ui';
import { catalogService, isLikelyTcgSetCode, normalizeTcgSetCode } from '@/services';
import { useSettingsStore } from '@/stores';
import type { Printing } from '@/types';
import { TcgCardSearchResults } from '../components/TcgCardSearchResults';
import { getSuggestionSetCodes, mergePrintingsByIdentity } from '../utils/tcgSearch';

const SEARCH_DEBOUNCE_MS = 400;
const MIN_SEARCH_LENGTH = 3;
const ITEMS_PER_PAGE = 12;

export function TcgCardsPage(): JSX.Element {
  const { t } = useTranslation('catalog');
  const [searchParams, setSearchParams] = useSearchParams();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const currency = useSettingsStore((s) => s.currency);

  const initialSearch = searchParams.get('q') || '';
  const pageParam = searchParams.get('page');
  const parsedPage = pageParam ? parseInt(pageParam, 10) : 1;
  const initialPage = Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;

  const [searchInput, setSearchInput] = useState(initialSearch);
  const [searchQuery, setSearchQuery] = useState(initialSearch.trim());
  const [currentPage, setCurrentPage] = useState(initialPage);

  const focusSearchInput = () => {
    const input = searchContainerRef.current?.querySelector('input');
    input?.focus();
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      if (next.length === 0 || next.length >= MIN_SEARCH_LENGTH) {
        setSearchQuery(next);
      } else {
        setSearchQuery('');
      }
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (currentPage > 1) params.set('page', currentPage.toString());

    setSearchParams(params, { replace: true });
  }, [currentPage, searchQuery, setSearchParams]);

  useEffect(() => {
    if (searchInput.length === 0) return undefined;

    const focusTimer = window.setTimeout(() => {
      const input = searchContainerRef.current?.querySelector('input');
      if (input && document.activeElement !== input) {
        input.focus();
      }
    }, 0);

    return () => window.clearTimeout(focusTimer);
  }, [searchInput.length, searchQuery]);

  const skip = (currentPage - 1) * ITEMS_PER_PAGE;
  const isSetCodeSearch = isLikelyTcgSetCode(searchQuery);

  const { data: printings, isLoading, error } = useQuery({
    queryKey: ['tcg-printings', searchQuery, currentPage, currency],
    queryFn: async () => {
      if (searchQuery.trim()) {
        if (isSetCodeSearch) {
          return catalogService.getPrintings({
            setCode: normalizeTcgSetCode(searchQuery),
            skip,
            take: ITEMS_PER_PAGE,
            currency,
          });
        }

        return catalogService.searchPrintings({
          q: searchQuery,
          skip,
          take: ITEMS_PER_PAGE,
          currency,
        });
      }

      return catalogService.getPrintings({ skip, take: ITEMS_PER_PAGE, currency });
    },
    staleTime: 10 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30 * 1000,
  });

  const {
    data: cardSuggestions = [],
    isLoading: isSuggestionsLoading,
    error: suggestionsError,
  } = useQuery({
    queryKey: ['tcg-card-suggestions', searchQuery, currency],
    queryFn: () =>
      catalogService.searchTcgCardSuggestions({
        q: searchQuery,
        take: 10,
        currency,
      }),
    enabled: searchQuery.length >= MIN_SEARCH_LENGTH,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const suggestionSetCodes = useMemo(
    () => getSuggestionSetCodes(cardSuggestions),
    [cardSuggestions],
  );

  const {
    data: associatedPrintings = [],
    isLoading: isAssociatedLoading,
    error: associatedError,
  } = useQuery({
    queryKey: ['tcg-associated-printings', currency, suggestionSetCodes],
    queryFn: () =>
      catalogService.getPrintingsBySetCodes(suggestionSetCodes, {
        currency,
        takePerCode: 8,
      }),
    enabled: suggestionSetCodes.length > 0,
    staleTime: 10 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30 * 1000,
  });

  const printingsArray = useMemo(
    () => mergePrintingsByIdentity((printings ?? []) as Printing[], associatedPrintings),
    [associatedPrintings, printings],
  );
  const isLastPage = !printings || ((printings ?? []) as Printing[]).length < ITEMS_PER_PAGE;
  const hasResults = printingsArray.length > 0 || cardSuggestions.length > 0;
  const showInitialLoading = isLoading && !hasResults;
  const inputNeedsMoreChars =
    searchInput.trim().length > 0 && searchInput.trim().length < MIN_SEARCH_LENGTH;

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPageToShow = isLastPage ? currentPage : currentPage + 1;

    if (currentPage > 1) {
      pages.push(1);
    }

    if (currentPage > 3) {
      pages.push('...');
    }

    for (let i = Math.max(1, currentPage - 1); i <= maxPageToShow; i += 1) {
      if (i !== 1 || currentPage <= 3) {
        if (i > 0 && !pages.includes(i)) {
          pages.push(i);
        }
      }
    }

    if (!isLastPage) {
      pages.push('...');
    }

    return pages;
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCurrentPage(1);
    focusSearchInput();
  };

  const searchBar = (
    <div ref={searchContainerRef} className="mb-6 w-full max-w-md">
      <div className="relative">
        <SearchBar
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder={t('list.search.placeholder')}
          autoFocus
          className={searchInput ? 'pr-10' : undefined}
        />
        {searchInput ? (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xl leading-none text-brand-700/70 hover:text-brand-950"
            type="button"
            aria-label={t('list.search.clearAria')}
          >
            x
          </button>
        ) : null}
      </div>
      {inputNeedsMoreChars ? (
        <p className="mt-1 font-sans text-sm text-brand-700">
          {t('list.search.hintMinChars')}
        </p>
      ) : null}
      {isSuggestionsLoading ? (
        <p className="mt-1 font-sans text-sm text-brand-700">
          {t('list.search.loadingSuggestions')}
        </p>
      ) : null}
      {isAssociatedLoading ? (
        <p className="mt-1 font-sans text-sm text-brand-700">
          {t('offers.searchingAssociated')}
        </p>
      ) : null}
      {suggestionsError ? (
        <p className="mt-1 font-sans text-sm text-amber-800">
          {t('list.search.suggestionsError')}
        </p>
      ) : null}
      {associatedError ? (
        <p className="mt-1 font-sans text-sm text-amber-800">
          {t('offers.associatedError')}
        </p>
      ) : null}
    </div>
  );

  if (showInitialLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <h1 className="mb-6 font-heading text-heading-xl text-brand-950">{t('list.title')}</h1>
        {searchBar}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="h-96 animate-pulse rounded-lg bg-brand-100" />
          ))}
        </div>
      </div>
    );
  }

  if (error && !hasResults) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <h1 className="mb-6 font-heading text-heading-xl text-brand-950">{t('list.title')}</h1>
        {searchBar}
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="font-sans text-lg text-red-600">{t('list.error.loadFailed')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <h1 className="mb-6 font-heading text-heading-xl text-brand-950">{t('list.title')}</h1>

      {searchBar}

      {!hasResults && !isLoading && !isSuggestionsLoading ? (
        <div className="flex min-h-[400px] flex-col items-center justify-center">
          {searchQuery ? (
            <>
              <div className="mb-4 font-sans text-lg text-brand-700">
                {t('list.empty.title', { query: searchQuery })}
              </div>
              <Button onClick={clearSearch}>{t('list.empty.resetButton')}</Button>
            </>
          ) : (
            <div className="font-sans text-lg text-brand-700">{t('list.empty.browse')}</div>
          )}
        </div>
      ) : (
        <TcgCardSearchResults
          printings={printingsArray}
          suggestions={cardSuggestions}
          query={searchQuery}
        />
      )}

      {!isLoading && printingsArray.length > 0 ? (
        <div className="mt-8 flex items-center justify-center gap-2">
          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            variant={currentPage === 1 ? 'outline' : 'primary'}
          >
            {t('pagination.prev')}
          </Button>

          <div className="flex gap-1">
            {getPageNumbers().map((page, index) => {
              if (page === '...') {
                return (
                  <span key={`ellipsis-${index}`} className="px-2 py-2 font-sans text-brand-700">
                    ...
                  </span>
                );
              }

              return (
                <Button
                  key={page}
                  onClick={() => setCurrentPage(page as number)}
                  variant={currentPage === page ? 'primary' : 'outline'}
                >
                  {page}
                </Button>
              );
            })}
          </div>

          {!isLastPage ? (
            <Button onClick={() => setCurrentPage((page) => page + 1)}>
              {t('pagination.next')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
